import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, QrCode, Clock, AlertCircle } from 'lucide-react';
import { eventsApi, registrationsApi } from '../api/client';
import { QRScanner } from '../components/QRScanner';
import { ScanResult } from '../components/ScanResult';
import { ManualTicketEntry } from '../components/ManualTicketEntry';

import { Event } from '../api/client';

interface ScanHistory {
  id: number;
  attendee_name: string;
  attendee_email: string;
  company: string;
  checked_in_at: string;
  ip_address?: string;
  performed_by?: string;
}

export const QRScannerPage: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [scanResult, setScanResult] = useState<any>(null);
  const [recentScans, setRecentScans] = useState<ScanHistory[]>([]);
  const [error, setError] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);

  // Load organizer's events
  useEffect(() => {
    const loadEvents = async () => {
      try {
        const events = await eventsApi.getOrganizerEvents();
        setEvents(events);
        
        // Auto-select if only one event
        if (events.length === 1) {
          setSelectedEventId(events[0].id);
        }
      } catch (err) {
        console.error('Failed to load events:', err);
        setError('Failed to load events');
      }
    };

    loadEvents();
  }, []);

  // Load scan history when event is selected
  useEffect(() => {
    if (selectedEventId) {
      loadScanHistory();
    }
  }, [selectedEventId]);

  const loadScanHistory = async () => {
    if (!selectedEventId) return;
    
    try {
      const response = await registrationsApi.getScanHistory(selectedEventId, 10);
      setRecentScans(response.recent_scans || []);
    } catch (err) {
      console.error('Failed to load scan history:', err);
    }
  };

  const handleScan = useCallback(async (ticketCode: string) => {
    if (isLoading) return;
    
    setIsLoading(true);
    setError('');
    
    try {
      const response = await registrationsApi.scanQRCode(ticketCode, navigator.userAgent);
      
      setScanResult(response);
      setIsScanning(false);
      
      // Reload scan history
      loadScanHistory();
      
      // Play different sound for already checked in
      if (response.already_checked_in) {
        const audio = new Audio('data:audio/wav;base64,UklGRiQCAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQACAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACAAAAAgAAAAIAAAACA');
        audio.play().catch(() => {});
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to scan ticket');
      // Play error sound
      const audio = new Audio('data:audio/wav;base64,UklGRl4CAABXQVZFZm10IBAAAAABAAEAIlYAAESsAAACABAAZGF0YToCAAB/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f39/f3==');
      audio.play().catch(() => {});
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, selectedEventId]);

  const handleScanNext = () => {
    setScanResult(null);
    setIsScanning(true);
    setShowManualEntry(false);
  };

  const selectedEvent = events.find(e => e.id === selectedEventId);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/organizer/dashboard')}
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">QR Code Scanner</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Event Selection */}
        {events.length > 1 && (
          <div className="mb-6">
            <label htmlFor="event" className="block text-sm font-medium text-gray-700 mb-2">
              Select Event
            </label>
            <select
              id="event"
              value={selectedEventId || ''}
              onChange={(e) => setSelectedEventId(Number(e.target.value))}
              className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Choose an event...</option>
              {events.map(event => (
                <option key={event.id} value={event.id}>
                  {event.title} - {new Date(event.date_time).toLocaleDateString()}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedEvent && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Scanner Section */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold flex items-center">
                    <QrCode className="h-6 w-6 mr-2" />
                    Scan Tickets
                  </h2>
                  <button
                    onClick={() => {
                      setShowManualEntry(!showManualEntry);
                      setIsScanning(!showManualEntry);
                    }}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    {showManualEntry ? 'Use Camera' : 'Manual Entry'}
                  </button>
                </div>

                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                    <p className="text-red-700">{error}</p>
                  </div>
                )}

                {showManualEntry ? (
                  <ManualTicketEntry onSubmit={handleScan} isLoading={isLoading} />
                ) : (
                  <>
                    {!isScanning ? (
                      <div className="text-center py-12">
                        <QrCode className="h-24 w-24 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 mb-6">Ready to scan tickets</p>
                        <button
                          onClick={() => setIsScanning(true)}
                          className="bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Start Scanning
                        </button>
                      </div>
                    ) : (
                      <QRScanner onScan={handleScan} isActive={isScanning && !isLoading} />
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Stats and Recent Scans */}
            <div className="space-y-6">
              {/* Event Stats */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold mb-4">Event Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Total Registered</span>
                    <span className="font-semibold">{selectedEvent.total_registrations}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Checked In</span>
                    <span className="font-semibold text-green-600">
                      {selectedEvent.checked_in_count}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Remaining</span>
                    <span className="font-semibold">
                      {selectedEvent.total_registrations - selectedEvent.checked_in_count}
                    </span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Check-in Rate</span>
                    <span className="font-semibold">
                      {selectedEvent.total_registrations > 0
                        ? Math.round((selectedEvent.checked_in_count / selectedEvent.total_registrations) * 100)
                        : 0}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Recent Scans */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <Clock className="h-5 w-5 mr-2" />
                  Recent Check-ins
                </h3>
                {recentScans.length === 0 ? (
                  <p className="text-gray-500 text-sm">No recent check-ins</p>
                ) : (
                  <div className="space-y-3">
                    {recentScans.map((scan) => (
                      <div key={scan.id} className="border-b pb-3 last:border-0">
                        <p className="font-medium text-gray-900">{scan.attendee_name}</p>
                        <p className="text-sm text-gray-600">{scan.company}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(scan.checked_in_at).toLocaleTimeString()}
                          {scan.performed_by && ` by ${scan.performed_by}`}
                        </p>
                        {scan.ip_address && (
                          <p className="text-xs text-gray-400">IP: {scan.ip_address}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Scan Result Modal */}
        {scanResult && (
          <ScanResult
            result={scanResult}
            onClose={() => setScanResult(null)}
            onScanNext={handleScanNext}
          />
        )}
      </div>
    </div>
  );
};